import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faRocket, faGear, faUserSecret, faHeart, 
  faTrophy, faChartLine, faCircleQuestion, 
  faLightbulb, faCircleCheck, faCircleExclamation,
  faImage, faCircleInfo
} from '@fortawesome/free-solid-svg-icons';

const GuideContainer = styled.div`
  max-width: 1000px;
  margin: 60px auto;
  padding: 0 20px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(to right, ${theme.colors.primary}, ${theme.colors.accent});
    border-radius: 2px;
  }
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 20px;
  background: linear-gradient(to right, ${theme.colors.primary}, ${theme.colors.accent});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const PageDescription = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
  flex-wrap: wrap;
  gap: 10px;
  
  @media (max-width: 768px) {
    justify-content: flex-start;
    overflow-x: auto;
    padding-bottom: 10px;
  }
`;

const Tab = styled.button`
  background-color: ${props => props.active ? theme.colors.primary : 'rgba(255, 255, 255, 0.05)'};
  color: ${props => props.active ? theme.colors.secondary : theme.colors.textSecondary};
  border: none;
  padding: 12px 24px;
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background-color: ${props => props.active ? theme.colors.primary : 'rgba(255, 255, 255, 0.1)'};
    transform: translateY(-2px);
  }
  
  @media (max-width: 480px) {
    padding: 10px 16px;
    font-size: 0.9rem;
  }
`;

const GuideSection = styled.section`
  margin-bottom: 60px;
  background-color: rgba(46, 46, 46, 0.4);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  @media (max-width: 768px) {
    padding: 30px;
  }
  
  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 30px;
  gap: 15px;
`;

const SectionIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent});
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.secondary};
  font-size: 1.5rem;
  flex-shrink: 0;
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const StepsContainer = styled.div`
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 25px;
    width: 2px;
    background: linear-gradient(to bottom, ${theme.colors.primary}40, ${theme.colors.accent}40);
  }
`;

const StepContainer = styled.div`
  margin-bottom: 40px;
  padding-left: 60px;
  position: relative;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 480px) {
    padding-left: 50px;
  }
`;

const StepMarker = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent});
  color: ${theme.colors.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.2rem;
  z-index: 1;
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
`;

const StepContent = styled.div`
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  padding: 30px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const StepTitle = styled.h3`
  font-size: 1.5rem;
  margin-top: 0;
  margin-bottom: 20px;
  color: ${theme.colors.primary};
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const StepDescription = styled.div`
  color: ${theme.colors.textSecondary};
  line-height: 1.7;
  
  p {
    margin-bottom: 15px;
  }
  
  ul, ol {
    margin-left: 20px;
    margin-bottom: 15px;
    
    li {
      margin-bottom: 10px;
    }
  }
  
  code {
    background-color: rgba(255, 255, 255, 0.1);
    padding: 3px 6px;
    border-radius: 4px;
    font-family: monospace;
    color: ${theme.colors.accent};
  }
  
  strong {
    color: ${theme.colors.secondary};
  }
`;

const NoteBox = styled.div`
  background-color: rgba(255, 255, 255, 0.05);
  border-left: 4px solid ${props => props.type === 'warning' ? theme.colors.accent : theme.colors.primary};
  padding: 15px 20px;
  margin: 20px 0;
  border-radius: 0 8px 8px 0;
  display: flex;
  align-items: flex-start;
  gap: 15px;
  
  .icon {
    color: ${props => props.type === 'warning' ? theme.colors.accent : theme.colors.primary};
    font-size: 1.2rem;
    margin-top: 2px;
  }
  
  .content {
    flex: 1;
  }
  
  strong {
    color: ${props => props.type === 'warning' ? theme.colors.accent : theme.colors.primary};
    display: block;
    margin-bottom: 5px;
  }
`;

const ImageContainer = styled.div`
  background-color: rgba(255, 255, 255, 0.03);
  border: 1px dashed rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
  text-align: center;
  color: ${theme.colors.textSecondary};
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
    border-color: ${theme.colors.primary}40;
  }
  
  p {
    margin: 10px 0 0;
    font-style: italic;
    font-size: 0.9rem;
  }
`;

const CommandExample = styled.div`
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 15px;
  margin: 15px 0;
  font-family: monospace;
  position: relative;
  overflow: hidden;
  
  &:before {
    content: 'Comando';
    position: absolute;
    top: 5px;
    right: 10px;
    font-size: 0.7rem;
    color: ${theme.colors.textSecondary};
    text-transform: uppercase;
    letter-spacing: 1px;
  }
`;

const Guide = () => {
  const [activeTab, setActiveTab] = useState('primeros-pasos');
  
  const renderTabContent = () => {
    switch(activeTab) {
      case 'primeros-pasos':
        return (
          <GuideSection>
            <SectionHeader>
              <SectionIcon>
                <FontAwesomeIcon icon={faRocket} />
              </SectionIcon>
              <SectionTitle>Primeros Pasos</SectionTitle>
            </SectionHeader>
            
            <StepsContainer>
              <StepContainer>
                <StepMarker>1</StepMarker>
                <StepContent>
                  <StepTitle>Invitar el Bot a tu Servidor</StepTitle>
                  <StepDescription>
                    <p>
                      Para comenzar, necesitas invitar a TuSecreto Bot a tu servidor de Discord. 
                      Sigue estos pasos:
                    </p>
                    <ol>
                      <li>
                        Haz clic en el botón <strong>"Invitar Bot"</strong> en la página de inicio 
                        o usa <a href="https://discord.com/oauth2/authorize?client_id=1380674741685321758&permissions=8&redirect_uri=https%3A%2F%2Flpxmpqpmoofsrlbenatl.supabase.co%2Fauth%2Fv1%2Fcallback&integration_type=0&scope=bot" target="_blank" rel="noopener noreferrer">este enlace</a>.
                      </li>
                      <li>Selecciona el servidor donde quieres añadir el bot.</li>
                      <li>Concede los permisos necesarios.</li>
                      <li>Completa la verificación si se te solicita.</li>
                      <li>¡Listo! El bot ahora está en tu servidor.</li>
                    </ol>
                    
                    <NoteBox>
                      <div className="icon">
                        <FontAwesomeIcon icon={faCircleInfo} />
                      </div>
                      <div className="content">
                        <strong>Nota</strong>
                        Necesitas tener permisos de administrador en el servidor para poder invitar al bot.
                      </div>
                    </NoteBox>
                    
                    <ImageContainer>
                      <FontAwesomeIcon icon={faImage} size="3x" />
                      <p>Captura de pantalla del proceso de invitación del bot</p>
                    </ImageContainer>
                  </StepDescription>
                </StepContent>
              </StepContainer>
              
              <StepContainer>
                <StepMarker>2</StepMarker>
                <StepContent>
                  <StepTitle>Configurar el Canal de Secretos</StepTitle>
                  <StepDescription>
                    <p>
                      Una vez que el bot esté en tu servidor, necesitas configurar un canal donde se enviarán los secretos:
                    </p>
                    <ol>
                      <li>Crea un nuevo canal de texto en tu servidor (por ejemplo, #secretos).</li>
                      <li>Usa el siguiente comando y selecciona el canal que acabas de crear:</li>
                    </ol>
                    
                    <CommandExample>
                      /config-secretos canal
                    </CommandExample>
                    
                    <p>
                      El bot confirmará que el canal ha sido configurado correctamente con un mensaje de confirmación.
                    </p>
                    
                    <p>
                      Es recomendable configurar los permisos del canal para que solo el bot pueda enviar mensajes, 
                      pero que todos puedan leer los secretos.
                    </p>
                    
                    <ImageContainer>
                      <FontAwesomeIcon icon={faImage} size="3x" />
                      <p>Configuración del canal de secretos</p>
                    </ImageContainer>
                  </StepDescription>
                </StepContent>
              </StepContainer>
              
              <StepContainer>
                <StepMarker>3</StepMarker>
                <StepContent>
                  <StepTitle>Activar el Sistema de Secretos</StepTitle>
                  <StepDescription>
                    <p>
                      Después de configurar el canal, necesitas activar el sistema de secretos:
                    </p>
                    <ol>
                      <li>Usa el siguiente comando para activar el sistema:</li>
                    </ol>
                    
                    <CommandExample>
                      /config-secretos activar
                    </CommandExample>
                    
                    <p>El bot confirmará que el sistema ha sido activado.</p>
                    
                    <p>Opcionalmente, puedes activar los secretos globales con:</p>
                    
                    <CommandExample>
                      /config-secretos global-activar
                    </CommandExample>
                    
                    <NoteBox type="warning">
                      <div className="icon">
                        <FontAwesomeIcon icon={faCircleExclamation} />
                      </div>
                      <div className="content">
                        <strong>Importante</strong>
                        Solo los administradores del servidor pueden cambiar la configuración del bot.
                      </div>
                    </NoteBox>
                  </StepDescription>
                </StepContent>
              </StepContainer>
            </StepsContainer>
          </GuideSection>
        );
        
      case 'usando-bot':
        return (
          <GuideSection>
            <SectionHeader>
              <SectionIcon>
                <FontAwesomeIcon icon={faUserSecret} />
              </SectionIcon>
              <SectionTitle>Usando el Bot</SectionTitle>
            </SectionHeader>
            
            <StepsContainer>
              <StepContainer>
                <StepMarker>1</StepMarker>
                <StepContent>
                  <StepTitle>Enviar un Secreto</StepTitle>
                  <StepDescription>
                    <p>
                      Cualquier miembro del servidor puede enviar un secreto anónimo:
                    </p>
                    <ol>
                      <li>Usa el siguiente comando en cualquier canal:</li>
                    </ol>
                    
                    <CommandExample>
                      /secreto contenido:"Tu secreto aquí" nsfw:No
                    </CommandExample>
                    
                    <p>
                      Reemplaza "Tu secreto aquí" con el secreto que quieras compartir.
                      Marca la opción nsfw como "Sí" si el contenido es para adultos.
                    </p>
                    
                    <p>
                      El secreto aparecerá en el canal de secretos sin mostrar quién lo envió, 
                      manteniendo tu anonimato.
                    </p>
                    
                    <ImageContainer>
                      <FontAwesomeIcon icon={faImage} size="3x" />
                      <p>Ejemplo de envío de un secreto</p>
                    </ImageContainer>
                  </StepDescription>
                </StepContent>
              </StepContainer>
              
              <StepContainer>
                <StepMarker>2</StepMarker>
                <StepContent>
                  <StepTitle>Reaccionar a los Secretos</StepTitle>
                  <StepDescription>
                    <p>
                      Puedes reaccionar a los secretos de forma anónima:
                    </p>
                    <ol>
                      <li>Debajo de cada secreto publicado, encontrarás botones con emojis (❤️, 😂, 😮, 😢, 😡).</li>
                      <li>Haz clic en el botón correspondiente al emoji con el que quieres reaccionar.</li>
                      <li>Tu reacción se registrará de forma anónima y se actualizará el contador de reacciones.</li>
                    </ol>
                    
                    <p>
                      Puedes quitar tu reacción haciendo clic nuevamente en el mismo botón.
                    </p>
                    
                    <ImageContainer>
                      <FontAwesomeIcon icon={faImage} size="3x" />
                      <p>Reacciones a un secreto</p>
                    </ImageContainer>
                  </StepDescription>
                </StepContent>
              </StepContainer>
              
              <StepContainer>
                <StepMarker>3</StepMarker>
                <StepContent>
                  <StepTitle>Ver tu Nivel</StepTitle>
                  <StepDescription>
                    <p>
                      El bot tiene un sistema de niveles basado en la cantidad de secretos que compartes:
                    </p>
                    
                    <CommandExample>
                      /nivel
                    </CommandExample>
                    
                    <p>
                      El bot te mostrará tu nivel actual, la cantidad de secretos compartidos 
                      y cuántos necesitas para subir al siguiente nivel.
                    </p>
                    
                    <p>
                      Cuantos más secretos compartas, más alto será tu nivel. 
                      ¡Compite con otros miembros por el nivel más alto!
                    </p>
                    
                    <ImageContainer>
                      <FontAwesomeIcon icon={faImage} size="3x" />
                      <p>Visualización del nivel de usuario</p>
                    </ImageContainer>
                  </StepDescription>
                </StepContent>
              </StepContainer>
              
              <StepContainer>
                <StepMarker>4</StepMarker>
                <StepContent>
                  <StepTitle>Ver el Ranking de Secretos</StepTitle>
                  <StepDescription>
                    <p>
                      Puedes ver los secretos más populares según las reacciones:
                    </p>
                    
                    <CommandExample>
                      /ranking tipo:local
                    </CommandExample>
                    
                    <p>
                      Especifica el tipo de ranking: <strong>local</strong> (solo este servidor) 
                      o <strong>global</strong> (todos los servidores).
                    </p>
                    
                    <p>
                      El bot mostrará los secretos más populares ordenados por número de reacciones.
                      El ranking se actualiza en tiempo real a medida que los usuarios reaccionan a los secretos.
                    </p>
                    
                    <ImageContainer>
                      <FontAwesomeIcon icon={faImage} size="3x" />
                      <p>Ranking de secretos populares</p>
                    </ImageContainer>
                  </StepDescription>
                </StepContent>
              </StepContainer>
            </StepsContainer>
          </GuideSection>
        );
        
      case 'consejos':
        return (
          <GuideSection>
            <SectionHeader>
              <SectionIcon>
                <FontAwesomeIcon icon={faLightbulb} />
              </SectionIcon>
              <SectionTitle>Consejos y Mejores Prácticas</SectionTitle>
            </SectionHeader>
            
            <StepsContainer>
              <StepContainer>
                <StepMarker>
                  <FontAwesomeIcon icon={faLightbulb} />
                </StepMarker>
                <StepContent>
                  <StepTitle>Mejores Prácticas</StepTitle>
                  <StepDescription>
                    <ul>
                      <li>
                        <strong>Moderación:</strong> Aunque los secretos son anónimos, asegúrate de establecer 
                        reglas claras sobre el tipo de contenido permitido en tu servidor.
                      </li>
                      <li>
                        <strong>Canal Dedicado:</strong> Es recomendable tener un canal exclusivo para secretos 
                        para mantener el servidor organizado y fácil de seguir.
                      </li>
                      <li>
                        <strong>Secretos Globales:</strong> Activa los secretos globales solo si estás cómodo con 
                        que los secretos de tu servidor se compartan con otros servidores.
                      </li>
                      <li>
                        <strong>Permisos del Canal:</strong> Configura los permisos del canal de secretos para que 
                        solo el bot pueda enviar mensajes, evitando así confusiones.
                      </li>
                      <li>
                        <strong>Promoción:</strong> Anima a los miembros de tu servidor a compartir secretos para 
                        mantener la actividad y el interés en el bot.
                      </li>
                    </ul>
                  </StepDescription>
                </StepContent>
              </StepContainer>
              
              <StepContainer>
                <StepMarker>
                  <FontAwesomeIcon icon={faCircleQuestion} />
                </StepMarker>
                <StepContent>
                  <StepTitle>Solución de Problemas Comunes</StepTitle>
                  <StepDescription>
                    <p><strong>El bot no responde a los comandos:</strong></p>
                    <ul>
                      <li>Verifica que el bot tenga los permisos necesarios en el servidor y en el canal.</li>
                      <li>Asegúrate de que el bot esté en línea.</li>
                      <li>Intenta reinvitar al bot si los problemas persisten.</li>
                    </ul>
                    
                    <p><strong>Los secretos no aparecen en el canal:</strong></p>
                    <ul>
                      <li>Verifica que el sistema esté activado con <code>/config-secretos estado</code>.</li>
                      <li>Asegúrate de que el canal esté correctamente configurado.</li>
                      <li>Comprueba que el bot tenga permisos para enviar mensajes en el canal de secretos.</li>
                    </ul>
                    
                    <p><strong>Las reacciones no funcionan:</strong></p>
                    <ul>
                      <li>Verifica que el bot tenga permisos para añadir reacciones y editar mensajes.</li>
                      <li>Intenta usar el comando <code>/ayuda</code> para verificar que el bot responde correctamente.</li>
                    </ul>
                    
                    <NoteBox>
                      <div className="icon">
                        <FontAwesomeIcon icon={faCircleInfo} />
                      </div>
                      <div className="content">
                        <strong>Soporte</strong>
                        Si encuentras algún problema que no puedes resolver, únete a nuestro <a href="https://discord.gg/pxHEVKftmg" target="_blank" rel="noopener noreferrer">servidor de soporte</a> para recibir ayuda.
                      </div>
                    </NoteBox>
                  </StepDescription>
                </StepContent>
              </StepContainer>
            </StepsContainer>
          </GuideSection>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <GuideContainer>
      <PageHeader>
        <PageTitle>Guía de TuSecreto Bot</PageTitle>
        <PageDescription>
          Aprende a configurar y utilizar TuSecreto Bot en tu servidor de Discord 
          con esta guía paso a paso.
        </PageDescription>
      </PageHeader>
      
      <TabsContainer>
        <Tab 
          active={activeTab === 'primeros-pasos'} 
          onClick={() => setActiveTab('primeros-pasos')}
        >
          <FontAwesomeIcon icon={faRocket} /> Primeros Pasos
        </Tab>
        <Tab 
          active={activeTab === 'usando-bot'} 
          onClick={() => setActiveTab('usando-bot')}
        >
          <FontAwesomeIcon icon={faUserSecret} /> Usando el Bot
        </Tab>
        <Tab 
          active={activeTab === 'consejos'} 
          onClick={() => setActiveTab('consejos')}
        >
          <FontAwesomeIcon icon={faLightbulb} /> Consejos
        </Tab>
      </TabsContainer>
      
      {renderTabContent()}
    </GuideContainer>
  );
};

export default Guide; 